import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Search, Plus, Upload, Download, FileText, Pencil, Trash2, Users as UsersIcon, Mail, Image as ImageIcon, Layers, ArrowRight } from "lucide-react";
import DateField from "@/components/app/DateField";

const POSITIONS = ["POR", "DEF", "MED", "DEL"];
const CLUB_SCOPE_ROLES = ["club_admin", "coordinator", "director_deportivo"];

export default function PlayersPage() {
  const { user } = useAuth();
  const canManage = CLUB_SCOPE_ROLES.includes(user?.role) || ["coach", "delegado"].includes(user?.role);
  const isClubScope = CLUB_SCOPE_ROLES.includes(user?.role);

  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterTeam, setFilterTeam] = useState("");
  const [filterPosition, setFilterPosition] = useState("");

  // Modal state
  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", jersey_number: "", position: "MED", dominant_foot: "Derecho", birthdate: "", team_id: "", parent_contacts: [] });

  // Import modal state
  const [showImport, setShowImport] = useState(false);
  const [importTeamId, setImportTeamId] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  // Selection state for bulk actions (send public link / upload photos)
  const [selected, setSelected] = useState(new Set());
  const [sendingBulk, setSendingBulk] = useState(false);
  const [showPhotosZip, setShowPhotosZip] = useState(false);
  const [zipTeamId, setZipTeamId] = useState("");
  const [zipFile, setZipFile] = useState(null);
  const [zipPreview, setZipPreview] = useState(null);
  const [uploadingZip, setUploadingZip] = useState(false);

  // Bulk-edit modal state (assign position/foot/team in bulk + jersey number per player)
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [bulkShared, setBulkShared] = useState({ position: "", dominant_foot: "", team_id: "" });
  const [bulkJerseys, setBulkJerseys] = useState({}); // { player_id: "7" }
  const [savingBulk, setSavingBulk] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, t, c] = await Promise.all([
        api.get("/players"),
        api.get("/teams"),
        api.get("/categories"),
      ]);
      setPlayers(p.data || []);
      setTeams(t.data || []);
      setCategories(c.data || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const teamById = useMemo(() => Object.fromEntries(teams.map(t => [t.id, t])), [teams]);
  const catById = useMemo(() => Object.fromEntries(categories.map(c => [c.id, c])), [categories]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return players
      .filter(p => {
        if (filterPosition && p.position !== filterPosition) return false;
        const team = teamById[p.team_id];
        if (filterTeam && p.team_id !== filterTeam) return false;
        if (filterCategory && team && team.category_id !== filterCategory) return false;
        if (term) {
          const hay = `${p.name || ""} ${p.jersey_number || ""} ${(teamById[p.team_id]?.name || "")}`.toLowerCase();
          if (!hay.includes(term)) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const ta = teamById[a.team_id]?.name || ""; const tb = teamById[b.team_id]?.name || "";
        if (ta !== tb) return ta.localeCompare(tb);
        return (a.jersey_number || 0) - (b.jersey_number || 0);
      });
  }, [players, search, filterCategory, filterTeam, filterPosition, teamById]);

  const teamsInCategory = filterCategory ? teams.filter(t => t.category_id === filterCategory) : teams;

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", jersey_number: "", position: "MED", dominant_foot: "Derecho", birthdate: "", team_id: filterTeam || (teams[0]?.id || ""), parent_contacts: [] });
    setShowEdit(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      jersey_number: p.jersey_number || "",
      position: p.position || "MED",
      dominant_foot: p.dominant_foot || "Derecho",
      birthdate: p.birthdate || "",
      team_id: p.team_id || "",
      parent_contacts: p.parent_contacts || [],
    });
    setShowEdit(true);
  };

  const savePlayer = async () => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    if (!form.team_id) { toast.error("Elige un equipo"); return; }
    try {
      const payload = {
        ...form,
        jersey_number: form.jersey_number === "" ? null : Number(form.jersey_number),
      };
      if (editing) {
        await api.put(`/players/${editing.id}`, payload);
        toast.success("Jugador actualizado");
      } else {
        await api.post("/players", payload);
        toast.success("Jugador creado");
      }
      setShowEdit(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deletePlayer = async (p) => {
    if (!window.confirm(`¿Eliminar a ${p.name}? Se perderá su histórico.`)) return;
    try {
      await api.delete(`/players/${p.id}`);
      toast.success("Jugador eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // ---- Bulk actions (only visible for admin/coord/DD) ----
  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAllVisible = (checked) => {
    setSelected((prev) => {
      if (!checked) return new Set();
      const next = new Set(prev);
      filtered.forEach((p) => next.add(p.id));
      return next;
    });
  };

  const sendBulkPublicLink = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Se enviará el enlace público por email a los padres/tutores de ${selected.size} jugador(es). ¿Continuar?`)) return;
    setSendingBulk(true);
    try {
      const { data } = await api.post("/players/bulk-send-public-link", { player_ids: Array.from(selected) });
      const s = data?.total_sent ?? 0;
      const sk = data?.total_skipped ?? 0;
      toast.success(`Enviado a ${s} jugador(es)${sk > 0 ? ` · ${sk} sin email` : ""}`);
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingBulk(false);
    }
  };

  // Bulk edit: prefill jerseys from selected players (empty when missing).
  const openBulkEdit = () => {
    if (selected.size === 0) return;
    const initialJerseys = {};
    players.forEach((p) => {
      if (selected.has(p.id)) initialJerseys[p.id] = p.jersey_number ?? "";
    });
    setBulkJerseys(initialJerseys);
    setBulkShared({ position: "", dominant_foot: "", team_id: "" });
    setShowBulkEdit(true);
  };

  const saveBulkEdit = async () => {
    const player_ids = Array.from(selected);
    const updates = {};
    if (bulkShared.position) updates.position = bulkShared.position;
    if (bulkShared.dominant_foot) updates.dominant_foot = bulkShared.dominant_foot;
    if (bulkShared.team_id) updates.team_id = bulkShared.team_id;
    const per_player = {};
    for (const [pid, val] of Object.entries(bulkJerseys)) {
      const original = players.find((p) => p.id === pid);
      const originalStr = original?.jersey_number == null ? "" : String(original.jersey_number);
      const cleaned = String(val).trim();
      if (cleaned !== originalStr && cleaned !== "") {
        per_player[pid] = { jersey_number: Number(cleaned) };
      }
    }
    if (Object.keys(updates).length === 0 && Object.keys(per_player).length === 0) {
      toast.error("No has indicado ningún cambio");
      return;
    }
    setSavingBulk(true);
    try {
      const { data } = await api.put("/players/bulk", { player_ids, updates, per_player });
      toast.success(`Actualizados ${data?.updated ?? 0} jugador(es)${data?.transfers ? ` · ${data.transfers} traspaso(s) registrado(s)` : ""}`);
      setShowBulkEdit(false);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingBulk(false);
    }
  };

  const runZipDry = async () => {
    if (!zipFile) { toast.error("Elige un ZIP"); return; }
    setUploadingZip(true);
    try {
      const fd = new FormData();
      fd.append("file", zipFile);
      const url = zipTeamId
        ? `/players/photos/bulk-upload?dry_run=true&team_id=${zipTeamId}`
        : `/players/photos/bulk-upload?dry_run=true`;
      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setZipPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadingZip(false);
    }
  };
  const runZipUpload = async () => {
    if (!zipFile) return;
    setUploadingZip(true);
    try {
      const fd = new FormData();
      fd.append("file", zipFile);
      const url = zipTeamId
        ? `/players/photos/bulk-upload?team_id=${zipTeamId}`
        : `/players/photos/bulk-upload`;
      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Subidas ${data?.total_matched ?? 0} fotos${data?.total_unmatched ? ` · ${data.total_unmatched} sin coincidencia` : ""}`);
      setShowPhotosZip(false);
      setZipFile(null); setZipPreview(null); setZipTeamId("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadingZip(false);
    }
  };

  const exportAll = async () => {
    try {
      const q = new URLSearchParams();
      if (filterCategory) q.set("category_id", filterCategory);
      if (filterTeam) q.set("team_id", filterTeam);
      const res = await api.get(`/players.csv?${q.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `jugadores-club-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadTemplate = async (fmt = "xlsx") => {
    try {
      const ext = fmt === "csv" ? "csv" : "xlsx";
      const res = await api.get(`/teams/players/template.${ext}`, { responseType: "blob" });
      const mime = ext === "csv" ? "text/csv" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url; a.download = `plantilla-jugadores.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runImportDry = async () => {
    if (!importFile || !importTeamId) { toast.error("Elige un equipo y un archivo"); return; }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post(`/teams/${importTeamId}/players/import?dry_run=true`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImporting(false);
    }
  };
  const runImport = async () => {
    if (!importFile || !importTeamId) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post(`/teams/${importTeamId}/players/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Importados ${data?.inserted ?? 0} jugadores`);
      setShowImport(false);
      setImportFile(null); setImportPreview(null); setImportTeamId("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="players-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <div className="overline text-primary">Panel del club</div>
          <h1 className="font-heading font-black text-5xl tracking-tighter uppercase mt-2">Jugadores</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Vista global de la plantilla del club. Filtra, busca, crea, edita, importa desde CSV o exporta a Excel sin tener que entrar equipo por equipo.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="players-download-template-btn" onClick={() => downloadTemplate("xlsx")}
            className="h-10 px-4 border border-border hover:border-primary hover:text-primary text-xs uppercase font-heading tracking-wider inline-flex items-center gap-2 transition-colors">
            <FileText size={14}/> Plantilla Excel
          </button>
          <button data-testid="players-download-template-csv-btn" onClick={() => downloadTemplate("csv")}
            className="h-10 px-3 border border-border hover:border-primary hover:text-primary text-[10px] uppercase font-heading tracking-wider inline-flex items-center gap-2 transition-colors" title="Descargar plantilla en CSV">
            CSV
          </button>
          <button data-testid="players-export-btn" onClick={exportAll}
            className="h-10 px-4 border border-border hover:border-primary hover:text-primary text-xs uppercase font-heading tracking-wider inline-flex items-center gap-2 transition-colors">
            <Download size={14}/> Exportar
          </button>
          {canManage && (
            <button data-testid="players-import-btn" onClick={() => setShowImport(true)}
              className="h-10 px-4 border border-border hover:border-primary hover:text-primary text-xs uppercase font-heading tracking-wider inline-flex items-center gap-2 transition-colors">
              <Upload size={14}/> Importar
            </button>
          )}
          {isClubScope && (
            <button data-testid="players-photos-zip-btn" onClick={() => setShowPhotosZip(true)}
              className="h-10 px-4 border border-border hover:border-primary hover:text-primary text-xs uppercase font-heading tracking-wider inline-flex items-center gap-2 transition-colors">
              <ImageIcon size={14}/> Fotos ZIP
            </button>
          )}
          {canManage && (
            <button data-testid="players-new-btn" onClick={openCreate}
              className="h-10 px-5 bg-primary text-white hover:bg-primary/90 text-xs uppercase font-heading tracking-wider inline-flex items-center gap-2 transition-colors">
              <Plus size={14}/> Nuevo jugador
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions bar — visible only when at least one player is selected */}
      {isClubScope && selected.size > 0 && (
        <div className="border-2 border-primary bg-primary/5 p-3 flex items-center justify-between flex-wrap gap-3" data-testid="players-bulk-bar">
          <div className="text-sm">
            <b className="text-primary">{selected.size}</b> jugador(es) seleccionado(s)
          </div>
          <div className="flex gap-2">
            <button data-testid="players-bulk-clear-btn" onClick={() => setSelected(new Set())}
              className="h-9 px-3 border border-border hover:border-white text-[11px] uppercase font-heading tracking-wider">
              Limpiar
            </button>
            <button data-testid="players-bulk-edit-btn" onClick={openBulkEdit}
              className="h-9 px-4 border border-primary text-primary hover:bg-primary hover:text-white text-[11px] uppercase font-heading tracking-wider inline-flex items-center gap-2 transition-colors">
              <Layers size={13}/> Editar en lote
            </button>
            <button data-testid="players-bulk-send-link-btn" onClick={sendBulkPublicLink} disabled={sendingBulk}
              className="h-9 px-4 bg-primary text-white hover:bg-primary/90 text-[11px] uppercase font-heading tracking-wider inline-flex items-center gap-2 disabled:opacity-60">
              <Mail size={13}/> {sendingBulk ? "Enviando…" : "Enviar enlace a padres"}
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative md:col-span-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="players-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, dorsal o equipo…"
            className="w-full h-10 pl-9 pr-3 bg-card border border-border text-sm focus:outline-none focus:border-primary transition-colors"
          />
        </div>
        <select data-testid="players-filter-category" value={filterCategory}
          onChange={(e) => { setFilterCategory(e.target.value); setFilterTeam(""); }}
          className="h-10 px-3 bg-card border border-border text-sm focus:outline-none focus:border-primary transition-colors">
          <option value="">Todas las categorías</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select data-testid="players-filter-team" value={filterTeam}
          onChange={(e) => setFilterTeam(e.target.value)}
          className="h-10 px-3 bg-card border border-border text-sm focus:outline-none focus:border-primary transition-colors">
          <option value="">Todos los equipos</option>
          {teamsInCategory.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select data-testid="players-filter-position" value={filterPosition}
          onChange={(e) => setFilterPosition(e.target.value)}
          className="h-10 px-3 bg-card border border-border text-sm focus:outline-none focus:border-primary transition-colors">
          <option value="">Todas las posiciones</option>
          {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-muted-foreground text-sm">Cargando…</div>
      ) : error ? (
        <div className="text-destructive text-sm" data-testid="players-error">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center">
          <UsersIcon size={28} className="mx-auto text-muted-foreground mb-3" />
          <div className="font-heading uppercase text-sm tracking-wider">Sin jugadores</div>
          <div className="text-muted-foreground text-xs mt-2">Prueba a limpiar los filtros o crea el primer jugador.</div>
        </div>
      ) : (
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="players-table">
            <thead className="bg-card">
              <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {isClubScope && (
                  <th className="w-8 py-2 px-2 font-heading">
                    <input type="checkbox" data-testid="players-select-all"
                      checked={filtered.length > 0 && filtered.every(p => selected.has(p.id))}
                      onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                      className="accent-primary cursor-pointer" />
                  </th>
                )}
                <th className="text-left py-2 px-3 font-heading">Dorsal</th>
                <th className="text-left py-2 px-3 font-heading">Nombre</th>
                <th className="text-left py-2 px-3 font-heading">Pos.</th>
                <th className="text-left py-2 px-3 font-heading">Equipo</th>
                <th className="text-left py-2 px-3 font-heading">Categoría</th>
                <th className="text-left py-2 px-3 font-heading">Nacimiento</th>
                <th className="text-right py-2 px-3 font-heading">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const t = teamById[p.team_id];
                const c = t && catById[t.category_id];
                return (
                  <tr key={p.id} data-testid={`players-row-${p.id}`}
                    className={`border-t border-border hover:bg-white/[0.02] transition-colors ${selected.has(p.id) ? "bg-primary/5" : ""}`}>
                    {isClubScope && (
                      <td className="py-2 px-2">
                        <input type="checkbox" data-testid={`players-select-${p.id}`}
                          checked={selected.has(p.id)}
                          onChange={() => toggleSelected(p.id)}
                          className="accent-primary cursor-pointer" />
                      </td>
                    )}
                    <td className="py-2 px-3 font-heading font-semibold">{p.jersey_number || "—"}</td>
                    <td className="py-2 px-3">
                      <Link to={`/player/${p.id}`} className="hover:text-primary transition-colors font-medium">
                        {p.name}
                      </Link>
                    </td>
                    <td className="py-2 px-3 overline text-[10px]">{p.position || "—"}</td>
                    <td className="py-2 px-3">
                      {t ? <Link to={`/team/${t.id}`} className="hover:text-primary transition-colors">{t.name}</Link> : "—"}
                    </td>
                    <td className="py-2 px-3 text-muted-foreground text-xs">{c?.name || "—"}</td>
                    <td className="py-2 px-3 text-xs text-muted-foreground">{p.birthdate || "—"}</td>
                    <td className="py-2 px-3">
                      <div className="flex justify-end gap-2">
                        {canManage && (
                          <button data-testid={`players-edit-${p.id}`} onClick={() => openEdit(p)}
                            className="w-8 h-8 border border-border hover:border-primary hover:text-primary flex items-center justify-center transition-colors" title="Editar">
                            <Pencil size={12} />
                          </button>
                        )}
                        {isClubScope && (
                          <button data-testid={`players-delete-${p.id}`} onClick={() => deletePlayer(p)}
                            className="w-8 h-8 border border-border hover:border-destructive hover:text-destructive flex items-center justify-center transition-colors" title="Eliminar">
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-3 py-2 border-t border-border text-[11px] text-muted-foreground bg-card">
            {filtered.length} de {players.length} jugadores mostrados
          </div>
        </div>
      )}

      {/* Edit / create modal */}
      {showEdit && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="players-modal">
          <div className="bg-card border border-border w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline text-primary">{editing ? "Editar jugador" : "Nuevo jugador"}</div>
                <h2 className="font-heading font-black text-2xl uppercase mt-1">{editing ? editing.name : "Nuevo jugador"}</h2>
              </div>
              <button onClick={() => setShowEdit(false)} className="text-muted-foreground hover:text-white" data-testid="players-modal-close">✕</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nombre completo *">
                <input data-testid="players-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full h-10 px-3 bg-background border border-border text-sm focus:outline-none focus:border-primary" />
              </Field>
              <Field label="Dorsal">
                <input data-testid="players-form-jersey" type="number" value={form.jersey_number} onChange={(e) => setForm({ ...form, jersey_number: e.target.value })}
                  className="w-full h-10 px-3 bg-background border border-border text-sm focus:outline-none focus:border-primary" />
              </Field>
              <Field label="Posición">
                <select data-testid="players-form-position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })}
                  className="w-full h-10 px-3 bg-background border border-border text-sm focus:outline-none focus:border-primary">
                  {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Pie dominante">
                <select data-testid="players-form-foot" value={form.dominant_foot} onChange={(e) => setForm({ ...form, dominant_foot: e.target.value })}
                  className="w-full h-10 px-3 bg-background border border-border text-sm focus:outline-none focus:border-primary">
                  <option>Derecho</option><option>Zurdo</option><option>Ambidiestro</option>
                </select>
              </Field>
              <Field label="Fecha de nacimiento">
                <DateField testid="players-form-birthdate" value={form.birthdate} onChange={(v) => setForm({ ...form, birthdate: v })} />
              </Field>
              <Field label="Equipo *">
                <select data-testid="players-form-team" value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })}
                  className="w-full h-10 px-3 bg-background border border-border text-sm focus:outline-none focus:border-primary">
                  <option value="">Selecciona equipo</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </Field>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setShowEdit(false)} className="h-10 px-4 border border-border hover:border-primary hover:text-primary text-xs uppercase font-heading tracking-wider" data-testid="players-form-cancel">Cancelar</button>
              <button onClick={savePlayer} className="h-10 px-5 bg-primary text-white hover:bg-primary/90 text-xs uppercase font-heading tracking-wider" data-testid="players-form-save">Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Import modal */}
      {showImport && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="players-import-modal">
          <div className="bg-card border border-border w-full max-w-xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline text-primary">Importar desde Excel o CSV</div>
                <h2 className="font-heading font-black text-2xl uppercase mt-1">Importar jugadores</h2>
              </div>
              <button onClick={() => { setShowImport(false); setImportPreview(null); setImportFile(null); }}
                className="text-muted-foreground hover:text-white" data-testid="players-import-close">✕</button>
            </div>
            <p className="text-xs text-muted-foreground">La importación se hace por equipo. Elige el equipo destino y sube el <b>Excel</b> (.xlsx) o CSV con las columnas indicadas en la plantilla. Solo <b>nombre</b> es obligatorio — el resto (dorsal, posición, pie dominante, datos de padres/tutores…) se puede completar después.</p>
            <Field label="Equipo destino *">
              <select data-testid="players-import-team" value={importTeamId} onChange={(e) => { setImportTeamId(e.target.value); setImportPreview(null); }}
                className="w-full h-10 px-3 bg-background border border-border text-sm focus:outline-none focus:border-primary">
                <option value="">Selecciona equipo</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </Field>
            <Field label="Archivo Excel (.xlsx) o CSV">
              <input data-testid="players-import-file" type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportPreview(null); }}
                className="w-full text-xs" />
            </Field>
            {importPreview && (
              <div className="border border-border p-3 text-xs space-y-1" data-testid="players-import-preview">
                <div><b>Filas leídas:</b> {importPreview.parsed_rows ?? 0}</div>
                <div><b>Válidas:</b> {importPreview.valid_players ?? 0}</div>
                <div><b>Errores:</b> {(importPreview.errors || []).length}</div>
                {(importPreview.errors || []).length > 0 && (
                  <ul className="list-disc pl-5 text-destructive">
                    {(importPreview.errors || []).slice(0, 5).map((err, i) => <li key={i}>Fila {err.row}: {err.error}</li>)}
                  </ul>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={runImportDry} disabled={!importFile || !importTeamId || importing}
                className="h-10 px-4 border border-border hover:border-primary hover:text-primary text-xs uppercase font-heading tracking-wider disabled:opacity-50" data-testid="players-import-preview-btn">Vista previa</button>
              <button onClick={runImport} disabled={!importPreview || importing || (importPreview?.valid_players ?? 0) === 0}
                className="h-10 px-5 bg-primary text-white hover:bg-primary/90 text-xs uppercase font-heading tracking-wider disabled:opacity-50" data-testid="players-import-confirm-btn">
                {importing ? "Importando…" : `Importar ${importPreview?.valid_players ?? 0} jugadores`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk photos ZIP modal */}
      {showPhotosZip && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="players-photos-zip-modal">
          <div className="bg-card border border-border w-full max-w-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline text-primary">Subida masiva de fotos</div>
                <h2 className="font-heading font-black text-2xl uppercase mt-1">Subir fotos (.zip)</h2>
              </div>
              <button onClick={() => { setShowPhotosZip(false); setZipFile(null); setZipPreview(null); }} className="text-muted-foreground hover:text-white" data-testid="players-photos-zip-close">✕</button>
            </div>
            <div className="text-xs text-muted-foreground space-y-2">
              <p>Sube un archivo <b>.zip</b> con las fotos de los jugadores. El sistema asocia cada imagen al jugador correspondiente usando el nombre del archivo.</p>
              <p><b>Ejemplos de nombres válidos:</b> <code>Iker Rodriguez.jpg</code> · <code>07-Iker.png</code> · <code>Iker-07.webp</code> · <code>7.jpg</code> (si el dorsal es único en el equipo)</p>
              <p><b>Formatos aceptados:</b> JPG, PNG, WEBP · Máx 5 MB por foto · Máx 100 MB por ZIP.</p>
            </div>
            <Field label="Equipo (opcional pero recomendado)">
              <select data-testid="players-photos-zip-team" value={zipTeamId}
                onChange={(e) => { setZipTeamId(e.target.value); setZipPreview(null); }}
                className="w-full h-10 px-3 bg-background border border-border text-sm focus:outline-none focus:border-primary">
                <option value="">Todos mis jugadores (puede haber dorsales duplicados)</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </Field>
            <Field label="Archivo ZIP">
              <input data-testid="players-photos-zip-file" type="file" accept=".zip,application/zip"
                onChange={(e) => { setZipFile(e.target.files?.[0] || null); setZipPreview(null); }}
                className="w-full text-xs" />
            </Field>
            {zipPreview && (
              <div className="border border-border p-3 text-xs space-y-1 max-h-64 overflow-auto" data-testid="players-photos-zip-preview">
                <div><b>Imágenes con coincidencia:</b> {zipPreview.total_matched}</div>
                <div><b>Sin coincidencia:</b> {zipPreview.total_unmatched}</div>
                {zipPreview.matched?.length > 0 && (
                  <div className="mt-2">
                    <div className="overline text-[10px]">Coincidencias</div>
                    <ul className="pl-4 list-disc">
                      {zipPreview.matched.slice(0, 10).map((m, i) => (
                        <li key={i}>{m.filename} → <b>{m.player_name}</b></li>
                      ))}
                    </ul>
                    {zipPreview.matched.length > 10 && <div className="text-muted-foreground">… y {zipPreview.matched.length - 10} más</div>}
                  </div>
                )}
                {zipPreview.unmatched?.length > 0 && (
                  <div className="mt-2 text-amber-500">
                    <div className="overline text-[10px]">Sin coincidencia</div>
                    <ul className="pl-4 list-disc">
                      {zipPreview.unmatched.slice(0, 10).map((m, i) => (
                        <li key={i}>{m.filename}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={runZipDry} disabled={!zipFile || uploadingZip}
                className="h-10 px-4 border border-border hover:border-primary hover:text-primary text-xs uppercase font-heading tracking-wider disabled:opacity-50" data-testid="players-photos-zip-preview-btn">Vista previa</button>
              <button onClick={runZipUpload} disabled={!zipPreview || uploadingZip || (zipPreview?.total_matched ?? 0) === 0}
                className="h-10 px-5 bg-primary text-white hover:bg-primary/90 text-xs uppercase font-heading tracking-wider disabled:opacity-50" data-testid="players-photos-zip-confirm-btn">
                {uploadingZip ? "Subiendo…" : `Subir ${zipPreview?.total_matched ?? 0} fotos`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk-edit modal */}
      {showBulkEdit && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="players-bulk-edit-modal">
          <div className="bg-card border border-border w-full max-w-3xl max-h-[92vh] overflow-y-auto p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline text-primary">Edición en lote</div>
                <h2 className="font-heading font-black text-2xl uppercase mt-1">
                  {selected.size} jugador{selected.size === 1 ? "" : "es"} seleccionado{selected.size === 1 ? "" : "s"}
                </h2>
              </div>
              <button onClick={() => setShowBulkEdit(false)} className="text-muted-foreground hover:text-white" data-testid="players-bulk-modal-close">✕</button>
            </div>

            {/* Shared fields */}
            <div>
              <div className="overline text-[10px] text-muted-foreground mb-3">
                Datos compartidos <span className="text-muted-foreground/60">(aplica a todos los seleccionados · deja &ldquo;Sin cambio&rdquo; para mantener)</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Posición">
                  <select data-testid="bulk-shared-position" value={bulkShared.position}
                    onChange={(e) => setBulkShared({ ...bulkShared, position: e.target.value })}
                    className="w-full h-10 px-3 bg-background border border-border text-sm focus:outline-none focus:border-primary">
                    <option value="">— Sin cambio —</option>
                    {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="Pie dominante">
                  <select data-testid="bulk-shared-foot" value={bulkShared.dominant_foot}
                    onChange={(e) => setBulkShared({ ...bulkShared, dominant_foot: e.target.value })}
                    className="w-full h-10 px-3 bg-background border border-border text-sm focus:outline-none focus:border-primary">
                    <option value="">— Sin cambio —</option>
                    <option>Derecho</option><option>Zurdo</option><option>Ambidiestro</option>
                  </select>
                </Field>
                <Field label="Cambiar de equipo">
                  <select data-testid="bulk-shared-team" value={bulkShared.team_id}
                    onChange={(e) => setBulkShared({ ...bulkShared, team_id: e.target.value })}
                    className="w-full h-10 px-3 bg-background border border-border text-sm focus:outline-none focus:border-primary">
                    <option value="">— Sin cambio —</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </Field>
              </div>
              {bulkShared.team_id && (
                <div className="mt-2 text-[11px] text-primary flex items-center gap-1">
                  <ArrowRight size={12}/> Se registrará un traspaso por cada jugador que cambie de equipo.
                </div>
              )}
            </div>

            {/* Per-player jersey numbers */}
            <div>
              <div className="overline text-[10px] text-muted-foreground mb-3">
                Dorsales individuales <span className="text-muted-foreground/60">(deja vacío para mantener el actual)</span>
              </div>
              <div className="border border-border max-h-72 overflow-y-auto" data-testid="bulk-jerseys-table">
                <table className="w-full text-sm">
                  <thead className="bg-background sticky top-0">
                    <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      <th className="text-left py-2 px-3 font-heading">Jugador</th>
                      <th className="text-left py-2 px-3 font-heading">Equipo actual</th>
                      <th className="text-left py-2 px-3 font-heading w-32">Dorsal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {players
                      .filter(p => selected.has(p.id))
                      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                      .map(p => {
                        const team = teamById[p.team_id];
                        return (
                          <tr key={p.id} className="border-t border-border">
                            <td className="py-2 px-3">{p.name}</td>
                            <td className="py-2 px-3 text-xs text-muted-foreground">{team?.name || "—"}</td>
                            <td className="py-2 px-3">
                              <input
                                data-testid={`bulk-jersey-${p.id}`}
                                type="number"
                                min="0"
                                max="99"
                                value={bulkJerseys[p.id] ?? ""}
                                onChange={(e) => setBulkJerseys({ ...bulkJerseys, [p.id]: e.target.value })}
                                className="w-20 h-9 px-2 bg-background border border-border text-sm focus:outline-none focus:border-primary"
                              />
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <button onClick={() => setShowBulkEdit(false)} data-testid="bulk-cancel-btn"
                className="h-10 px-4 border border-border hover:border-primary hover:text-primary text-xs uppercase font-heading tracking-wider">
                Cancelar
              </button>
              <button onClick={saveBulkEdit} disabled={savingBulk} data-testid="bulk-save-btn"
                className="h-10 px-5 bg-primary text-white hover:bg-primary/90 text-xs uppercase font-heading tracking-wider disabled:opacity-60">
                {savingBulk ? "Guardando…" : "Aplicar cambios"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <label className="overline text-[10px] text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}
